import React, { useRef, useState } from 'react';
import '../assets/styles/Contact.scss';
import emailjs from '@emailjs/browser';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/Send';
import TextField from '@mui/material/TextField';
import ReCAPTCHA from 'react-google-recaptcha';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Declare grecaptcha global variable
declare global {
  interface Window {
    grecaptcha: any;
  }
}

function Contact() {

  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [isSending, setIsSending] = useState<boolean>(false);

  const form = useRef();

  const sendEmail = (e: any) => {
    e.preventDefault();
    console.log('Form submitted:', { name, email, message, captcha });

    if (name !== '' && email !== '' && message !== '' && captcha) {
       var templateParams = {
         name: name,
         email: email,
         message: message,
         'g-recaptcha-response': captcha  // Add reCAPTCHA response
       };

       console.log('Sending email with params:', templateParams);
       
       // Show loading state using React state
       setIsSending(true);

       emailjs.send('service_qw2w9en', 'template_cs86thi', templateParams, 't7EUbgVr7QAKTBtCj').then(
         (response) => {
           console.log('SUCCESS!', response.status, response.text);
           toast.success('Message sent successfully!');
           setName('');
           setEmail('');
           setMessage('');
           setCaptcha(null);
           // Reset captcha widget
           if (window.grecaptcha) {
             window.grecaptcha.reset();
           }
         },
         (error) => {
           console.log('FAILED...', error);
           console.log('Error details:', {
             errorCode: error.text,
             errorMessage: error.message,
             errorStatus: error.status
           });
           
           // More specific error messages
           if (error.text === 'Bad Request') {
             toast.error('Invalid EmailJS configuration. Please check service ID, template ID, and public key.');
           } else if (error.text === 'Unauthorized') {
             toast.error('EmailJS authentication failed. Please check your public key.');
           } else if (error.text === 'Not Found') {
             toast.error('EmailJS service or template not found. Please check your configuration.');
           } else {
             toast.error(`Failed to send message: ${error.text || error.message}. Please try again.`);
           }
         }
       ).finally(() => {
         // Hide loading state using React state
         setIsSending(false);
       });
    } else {
      console.log('Form validation failed:', { name: !!name, email: !!email, message: !!message, captcha: !!captcha });
      if (!captcha) {
        toast.warning('Please complete the reCAPTCHA verification first.');
      } else {
        toast.warning('Please fill in all required fields.');
      }
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Name changed:', e.target.value);
    setName(e.target.value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Email changed:', e.target.value);
    setEmail(e.target.value);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Message changed:', e.target.value);
    setMessage(e.target.value);
  };

  return (
    <div id="contact">
      <div className="items-container">
        <div className="contact_wrapper">
          <h2 className="section-heading">Contact Me</h2>
          <p>Got a project waiting to be realized? Let's collaborate and make it happen!</p>
          
          <Box
            ref={form}
            component="form"
            noValidate
            autoComplete="off"
            className='contact-form'
            onSubmit={sendEmail}
          >
            <div className='form-flex'>
              <TextField
                required
                id="name"
                label="Your Name"
                placeholder="What's your name?"
                value={name}
                onChange={handleNameChange}
                variant="outlined"
                fullWidth
                style={{marginBottom: '15px'}}
              />
              <TextField
                required
                id="email"
                label="Email / Phone"
                placeholder="How can I reach you?"
                value={email}
                onChange={handleEmailChange}
                variant="outlined"
                fullWidth
                style={{marginBottom: '15px'}}
              />
            </div>
            <TextField
              required
              id="message"
              label="Message"
              placeholder="Send me any inquiries or questions"
              multiline
              rows={4}
              className="body-form"
              value={message}
              onChange={handleMessageChange}
              variant="outlined"
              fullWidth
              style={{marginBottom: '15px'}}
            />
            <ReCAPTCHA
              sitekey="6LfolrsrAAAAAMhJ-yaYhYzxezwnS78kp4CHg8h1"
              onChange={setCaptcha}
              onExpired={() => {
                console.log('reCAPTCHA expired');
                setCaptcha(null);
              }}
              onError={() => {
                console.log('reCAPTCHA error');
                setCaptcha(null);
              }}
              style={{marginBottom: '15px'}}
            />
            <Button 
              variant="contained" 
              endIcon={<SendIcon />} 
              type="submit"
              disabled={!captcha || isSending}
              style={{float: 'right'}}
            >
              {isSending ? 'Sending...' : 'Send'}
            </Button>
          </Box>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}

export default Contact;